import React, { useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, DownloadIcon, ExternalLinkIcon, MailIcon, PrinterIcon, RotateCcwIcon } from 'lucide-react';
import { ActionChecklist } from './ActionChecklist';
import { SavingsPillars } from './SavingsPillars';
import { CALENDLY_URL, CONTACT, DISCLAIMER, TIMEFRAME_OPTIONS } from '../../data/roadmapContent';
import { formatCurrency, formatDate } from '../../utils/roadmapCalcs';
import { generateRoadmapPdf, roadmapFileName } from '../../utils/roadmapPdf';
import type { RoadmapAnswers, RoadmapResults as Results } from '../../types/roadmap';
interface RoadmapResultsProps {
  answers: RoadmapAnswers;
  results: Results;
  setName: (name: string) => void;
  onToggleAction: (id: string) => void;
  onRestart: () => void;
}
function Figure({
  label,
  value,
  hint
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return <div data-id="element-358" data-mp="2f6txs7nnulaa">
      <dt className="text-sm text-slate-500 dark:text-slate-400" data-id="element-359" data-mp="2f6ke11t7lad1">{label}</dt>
      <dd className="mt-1 text-xl font-bold text-slate-900 dark:text-white tabular-nums" data-id="element-360" data-mp="31d7gsmm2s4k3">
        {value}
      </dd>
      {hint && <p className="mt-1 text-xs text-slate-400 dark:text-slate-500" data-id="element-361" data-mp="2wfo43r50zzu9">{hint}</p>}
    </div>;
}
export function RoadmapResults({
  answers,
  results,
  setName,
  onToggleAction,
  onRestart
}: RoadmapResultsProps) {
  const [busy, setBusy] = useState(false);
  const percent = Math.round(results.progress * 100);
  const spokeToBroker = answers.brokerStatus === 'yes';
  const timeframe = TIMEFRAME_OPTIONS.find(option => option.value === answers.timeframe)?.label ?? 'Not set';
  const download = async () => {
    setBusy(true);
    try {
      const doc = await generateRoadmapPdf(answers, results);
      doc.save(roadmapFileName(answers));
    } finally {
      setBusy(false);
    }
  };

  // No mail server exists, so the PDF is saved locally and the visitor's own
  // mail app opens with a pre-filled summary for them to attach it to.
  const emailIt = async () => {
    await download();
    const lines = ['My Property Savings Roadmap', '', `Total savings goal: ${formatCurrency(results.totalSavingsGoal)}`, `Available now: ${formatCurrency(results.usableSavings)}`, `Still to save: ${formatCurrency(results.savingsGap)}`, `Saving each pay: ${formatCurrency(answers.couldSavePerPay)}`, results.completionDate ? `Estimated completion: ${formatDate(results.completionDate)}` : '', '', 'The PDF has been saved to my downloads — please attach it before sending.', '', 'All figures are estimates only.'].filter(Boolean);
    window.location.href = `mailto:?subject=${encodeURIComponent('My Property Savings Roadmap')}&body=${encodeURIComponent(lines.join('\n'))}`;
  };
  const restart = () => {
    if (window.confirm('Starting again will clear your answers. Nothing has been saved. Continue?')) {
      onRestart();
    }
  };
  return <div className="w-full space-y-6" data-id="element-362" data-mp="1qjanom8g8j73">
      {/* Headline */}
      <header className="text-center" data-id="element-363" data-mp="1qkvlqltz2k9p">
        <p className="text-sm font-medium text-primary-600 dark:text-primary-400" data-id="element-364" data-mp="1ls2hdz2f1dy5">
          Your roadmap
        </p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-[1.15]" data-id="element-365" data-mp="1hrij2s63w4af">
          Here's what it takes
        </h1>
      </header>

      {/* Your savings roadmap — the hero card, deliberately the heaviest thing
          on the page, with the per-pay amount given the most weight because it
          is the only figure that can be acted on tomorrow. */}
      <section className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-xl shadow-slate-900/5 dark:shadow-none p-6 sm:p-8" data-id="element-366" data-mp="20fnjbpnk174h">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-367" data-mp="p2bilnn3d4mj">
          Your savings roadmap
        </h2>

        <p className="mt-6 text-sm text-slate-500 dark:text-slate-400" data-id="element-368" data-mp="k274235qujyy">
          {results.goalReached ? 'You have enough for' : 'Save this each pay'}
        </p>
        <p className="mt-1 text-4xl sm:text-5xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-primary-500 tabular-nums" data-id="element-369" data-mp="2cqwmhlotpmbc">
          {results.goalReached ? formatCurrency(results.totalSavingsGoal) : answers.couldSavePerPay > 0 ? formatCurrency(answers.couldSavePerPay) : 'Add an amount'}
        </p>
        {!results.goalReached && answers.payFrequency && <p className="mt-1 text-base text-slate-600 dark:text-slate-300" data-id="element-370" data-mp="2vn4ugx980bac">
            {answers.payFrequency}, until{' '}
            {results.completionDate ? formatDate(results.completionDate) : 'your goal is reached'}
          </p>}

        <div className="mt-8" data-id="element-371" data-mp="169j68w6kihoj">
          <div className="flex items-baseline justify-between mb-2" data-id="element-372" data-mp="16cnkynhnsvtg">
            <span className="text-sm text-slate-500 dark:text-slate-400" data-id="element-373" data-mp="1bayvz1joxcd9">
              Progress toward your goal
            </span>
            <span className="text-sm font-semibold text-primary-600 dark:text-primary-400" data-id="element-374" data-mp="13rpm7779vbno">
              {percent}%
            </span>
          </div>
          <div className="h-3 w-full rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden" data-id="element-375" data-mp="1cphys3lm057d">
            <div className="h-full rounded-full bg-gradient-to-r from-accent-500 to-primary-500 transition-[width] duration-300 ease-out" style={{
            width: `${percent}%`
          }} data-id="element-376" data-mp="17sj67phlg6ba" />
          </div>
        </div>

        <dl className="mt-8 pt-8 border-t border-slate-200 dark:border-white/10 grid grid-cols-2 gap-x-6 gap-y-6" data-id="element-377" data-mp="ts8i2u7o55tt">
          <Figure label="Total savings goal" value={formatCurrency(results.totalSavingsGoal)} data-id="element-378" data-mp="p0qioalanday" />
          <Figure label="Available now" value={formatCurrency(results.usableSavings)} data-id="element-379" data-mp="19l0vpvsmay0" />
          <Figure label="Still to save" value={formatCurrency(results.savingsGap)} data-id="element-380" data-mp="3sfusq1f04u04" />
          <Figure label="Estimated completion" value={results.completionDate ? formatDate(results.completionDate) : 'Not set'} hint={answers.couldSavePerPay > 0 ? undefined : 'Add a savings amount to see a date'} data-id="element-381" data-mp="21ddsb7g4eukb" />
        </dl>

        {results.behindTimeframe && <p className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-200 leading-relaxed" data-id="element-382" data-mp="1ifujo4xkrdyf">
            <AlertTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-383" data-mp="1rh106e084oro" />
            At this rate you'd reach your goal after the timeframe you picked.
            Worth either extending the timeframe, lifting the amount per pay, or
            looking at a lower property price.
          </p>}

        {results.goalReached && <p className="mt-6 flex items-start gap-3 rounded-2xl border border-primary-300 dark:border-primary-500/30 bg-primary-50 dark:bg-primary-500/10 p-4 text-sm text-primary-900 dark:text-primary-200 leading-relaxed" data-id="element-384" data-mp="n5o4lgnv9e6k">
            <CheckCircle2Icon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-385" data-mp="dgeeg6cl25l3" />
            You've already got the funds together for this goal. The next step is
            confirming the numbers against a real property.
          </p>}
      </section>

      {/* Your property goal — context, deliberately compact */}
      <section className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm dark:shadow-none p-6 sm:p-8" data-id="element-386" data-mp="3vmixc3ay9i6x">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-387" data-mp="3qor85h1owa6f">
          Your property goal
        </h2>
        <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-6" data-id="element-388" data-mp="1ovb49iglcyti">
          <Figure label="Buying" value={answers.goal === 'first-investment' ? 'First investment' : 'First home'} data-id="element-389" data-mp="1ovtc7ccm91h9" />
          <Figure label="Target price" value={formatCurrency(answers.propertyPrice)} data-id="element-390" data-mp="12qv7q7ukma99" />
          <Figure label="Target purchase" value={timeframe} data-id="element-391" data-mp="sype6too7kry" />
          {spokeToBroker && answers.brokerTarget > 0 && <Figure label="Your broker's target" value={formatCurrency(answers.brokerTarget)} hint={Math.abs(answers.brokerTarget - results.totalSavingsGoal) > 1000 ? 'Differs from our estimate — worth checking' : 'Close to our estimate'} data-id="element-392" data-mp="eyy43419yoj8" />}
        </dl>
      </section>

      {/* Your next step — branches on the broker answer */}
      <section className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm dark:shadow-none p-6 sm:p-8" data-id="element-393" data-mp="1zd897pg390ka">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-394" data-mp="3ox81ut95qw7y">
          Your next step
        </h2>
        {spokeToBroker ? <p className="mt-3 text-base text-slate-600 dark:text-slate-300 leading-relaxed" data-id="element-395" data-mp="3juza2080u07f">
            Keep your automatic savings running and review your position if your
            income, debts, expenses, timeframe or property goal changes.
          </p> : <>
            <p className="mt-3 text-lg font-semibold text-slate-900 dark:text-white" data-id="element-396" data-mp="bffkpcah11xn">
              Are you saving towards the right number?
            </p>
            <p className="mt-2 text-base text-slate-600 dark:text-slate-300 leading-relaxed" data-id="element-397" data-mp="lfpx4uoom5br">
              This roadmap gives you a starting estimate. The next step is
              checking your borrowing position and confirming how much you may
              actually need for the property you are trying to buy.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white text-lg font-semibold transition-[transform,box-shadow] duration-200 ease-out hover:shadow-[0_0_30px_rgba(20,184,166,0.35)] hover:scale-[1.02]" data-id="element-398" data-mp="2i9knndtv55sf">
              Book a Call With Liam
              <ExternalLinkIcon className="w-5 h-5" data-id="element-399" data-mp="472ow6bb2qyw" />
            </a>
          </>}
      </section>

      <SavingsPillars answers={answers} results={results} data-id="element-400" data-mp="3vf703rq50u2z" />

      <ActionChecklist checked={answers.checkedActions} onToggle={onToggleAction} data-id="element-401" data-mp="3qngjj40lxa34" />

      {/* Take it with you */}
      <section className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm dark:shadow-none p-6 sm:p-8" data-id="element-402" data-mp="3locbwhtbzkew">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-403" data-mp="i8f1c7r9cag1">
          Take it with you
        </h2>
        <label className="block mt-5" data-id="element-404" data-mp="qhks6kuqyx6w">
          <span className="block text-sm text-slate-500 dark:text-slate-400 mb-2" data-id="element-405" data-mp="qim56uvbptr9">
            Your name, so we can put it on the PDF (optional)
          </span>
          <input type="text" value={answers.name} onChange={event => setName(event.target.value)} placeholder="First and last name" autoComplete="name" className="w-full px-5 py-4 rounded-2xl border-2 border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-lg text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:border-primary-500 outline-none transition-colors" data-id="element-406" data-mp="zh7tl6nh82zy" />
        </label>

        <button type="button" onClick={download} disabled={busy} className="mt-5 inline-flex items-center justify-center gap-2 w-full px-8 py-5 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white text-lg font-semibold transition-[transform,box-shadow,opacity] duration-200 ease-out hover:shadow-[0_0_30px_rgba(20,184,166,0.35)] hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100" data-id="element-407" data-mp="1n5qu300abbhh">
          <DownloadIcon className="w-5 h-5" data-id="element-408" data-mp="1h7fa8rnzeud7" />
          {busy ? 'Preparing your PDF…' : 'Download My Savings Roadmap'}
        </button>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-409" data-mp="1cff5mlwyvdaa">
          <button type="button" onClick={() => window.print()} className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border border-slate-200 dark:border-white/10 text-base font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" data-id="element-410" data-mp="1q72a3912bs45">
            <PrinterIcon className="w-4 h-4" data-id="element-411" data-mp="1v4lpic9hyaxo" />
            Print
          </button>
          <button type="button" onClick={emailIt} disabled={busy} className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border border-slate-200 dark:border-white/10 text-base font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors disabled:opacity-60" data-id="element-412" data-mp="5sa3nckqcbob">
            <MailIcon className="w-4 h-4" data-id="element-413" data-mp="22peia4yyh0sq" />
            Email
          </button>
          <button type="button" onClick={restart} className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border border-slate-200 dark:border-white/10 text-base font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" data-id="element-414" data-mp="27oiocmql2ieu">
            <RotateCcwIcon className="w-4 h-4" data-id="element-415" data-mp="1jt676kj9hvpi" />
            Start again
          </button>
        </div>

        <p className="mt-4 text-xs text-slate-400 dark:text-slate-500 leading-relaxed" data-id="element-416" data-mp="1osiuf6e1p4sm">
          Your PDF is created on your own device. Nothing you entered has been
          sent or stored. Email opens a draft for you to attach the PDF to.
        </p>
      </section>

      <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed" data-id="element-417" data-mp="1j0wrtqfjb0h3">
        {DISCLAIMER}
      </p>
      <p className="text-xs text-slate-400 dark:text-slate-500" data-id="element-418" data-mp="1e3muzpptfmj8">
        {CONTACT.name} · {CONTACT.acn}
      </p>
    </div>;
}